import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import IdsAccordionHeader from '../ids-accordion/ids-accordion-header';
import IdsHideFocusMixin from '../../mixins/ids-hide-focus-mixin/ids-hide-focus-mixin';
import IdsModuleNavTextDisplayMixin from './ids-module-nav-text-display-mixin';
import IdsModuleNavDisplayModeMixin from './ids-module-nav-display-mode-mixin';

import styles from './ids-module-nav-item.scss';

const Base = IdsModuleNavDisplayModeMixin(
  IdsModuleNavTextDisplayMixin(
    IdsHideFocusMixin(
      IdsAccordionHeader
    )
  )
);

/**
 * IDS Module Nav Item Component
 * @type {IdsModuleNavItem}
 * @inherits IdsAccordionHeader
 * @mixes IdsHideFocusMixin
 * @mixes IdsModuleNavDisplayModeMixin
 * @mixes IdsModuleNavTextDisplayMixin
 * @part expander - this accoridon header's expander button element
 * @part header - the accordion header's root element
 * @part icon - the accordion header's icon element
 */
@customElement('ids-module-nav-item')
@scss(styles)
export default class IdsModuleNavItem extends Base {
  #hasBeenSelected = false;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.toggleExpanderIcon(this.panel && this.panel.isExpandable);
    this.#refreshTextNode();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes(): Array<any> {
    return [
      ...super.attributes,
      attributes.ICON
    ];
  }

  /**
   * @returns {Array<string>} List of available color variants for this component
   */
  colorVariants = [
    'app-menu',
    'sub-app-menu',
    'module-nav',
    'sub-module-nav'
  ];

  /**
   * Inner template contents
   * @returns {string} The template
   */
  template(): string {
    return `
      <div class="ids-module-nav-item ids-accordion-header" tabindex="0" part="header">
        <ids-icon class="ids-accordion-display-icon" part="display-icon"></ids-icon>
        <slot></slot>
        ${this.templateExpanderIcon()}
      </div>
    `;
  }

  /**
   * @returns {HTMLElement | null} Live reference to the container element
   */
  get containerEl(): HTMLElement | null {
    return this.shadowRoot?.querySelector(`.${this.name}`) || null;
  }

  onDisplayModeChange() {
    this.#refreshTextNode();
    this.#updateIconVisibility();
  }

  #refreshTextNode() {
    if (this.textNode) {
      this.textNode.audible = this.displayMode !== 'expanded';
      this.textNode.overflow = 'ellipsis';
    }
  }

  onTextDisplayChange(val: string) {
    if (this.textNode) this.textNode.audible = val !== 'default';
  }

  /**
   * Set the icon for the module nav item
   * @param {string | null} val - The icon name or null to remove
   */
  set icon(val: string | null) {
    if (val) {
      this.setAttribute(attributes.ICON, val);
    } else {
      this.removeAttribute(attributes.ICON);
    }

    this.#updateIconVisibility();
    this.refreshIconDisplay(val);
  }

  /**
   * Get the icon name
   * @returns {string | null} The icon name or null if not set
   */
  get icon() {
    return this.getAttribute(attributes.ICON);
  }

  #updateIconVisibility() {
    if (!this.container) {
      return;
    }
    const hasIcon = !!this.icon;
    const isCollapsed = this.container.classList.contains('display-mode-collapsed');

    if (!hasIcon && isCollapsed) {
      this.container.classList.add('no-icon-collapsed');
    } else {
      this.container.classList.remove('no-icon-collapsed');
    }
  }

  /**
   * @param {boolean} val true if this module nav item should appear "selected"
   */
  set selected(val: boolean) {
    const currentValue = stringToBool(this.getAttribute(attributes.SELECTED));
    const isValueTruthy = stringToBool(val);

    if (currentValue !== isValueTruthy) {
      if (isValueTruthy) {
        this.setAttribute(attributes.SELECTED, 'true');
      } else {
        this.removeAttribute(attributes.SELECTED);
      }
      this.#hasBeenSelected = isValueTruthy;
      this.#refreshSelected(isValueTruthy);

      if (isValueTruthy) {
        this.triggerEvent('selected', this, {
          bubbles: true,
          detail: {
            elem: this,
          }
        });
      }
    } else if (currentValue && isValueTruthy && this.#hasBeenSelected) {
      this.triggerEvent('reselected', this, {
        bubbles: true,
        detail: {
          elem: this,
        }
      });
    }
  }

  /**
   * @returns {boolean} true if this module nav item appears "selected"
   */
  get selected(): boolean {
    return stringToBool(this.getAttribute(attributes.SELECTED));
  }

  /**
   * Refreshes the visual "Selected" state
   * @param {boolean} isSelected true if the Module Nav Item should appear "Selected"
   */
  #refreshSelected(isSelected: boolean) {
    this.container?.classList[isSelected ? 'add' : 'remove']('selected');

    const textNode = this.textNode;
    if (textNode && this.colorVariant === 'app-menu') {
      textNode.fontWeight = isSelected ? 'bold' : null;
    }
  }
}
